import { ChangePasswordForm } from '@h2hspark/auth-ui';
import * as React from 'react';

import { PageBackLink } from '../../member/page-back-link';

import { MEMBER_ACCOUNT_COPY, MEMBER_PROFILE_ROUTES } from './constants';

/**
 * `/app/account/password` — **Change password, on its own page** (`USR-004` AC-3, `MEM-007` AC-6).
 *
 * ## Why a page, when the prototype's row has no destination at all
 *
 * `Web App.dc.html` §2.15 draws a *Change password* row in the Account Management card with no
 * handler behind it and no screen to open — the design's own gap, logged as
 * `handoff-corrections.md` #129. So where it goes is a judgement rather than a transcription, and
 * the plan of 2026-09-11 took it: *"Change password gets its own page; the prototype's row has
 * nowhere to go."*
 *
 * It was an inline card at `/app/account#password` until Phase 5. The merged Your account screen
 * is now a two-column layout with a profile form in it, and a password form folded into that
 * column would put **two unrelated Saves on one screen** — which is the thing `MEM-007` AC-4's
 * single pinned Cancel/Save footer exists to prevent. A member who typed a new password and then
 * pressed the footer's *Save changes* would have saved their job title.
 *
 * It is also the shape of the three sub-pages beside it: Privacy, Matching preferences and Social
 * channels are each one card, one Save and a back link to the account, and this is a fourth of the
 * same thing.
 *
 * ## `/app/account#password` still resolves
 *
 * The anchor is redirected in `App.tsx` rather than dropped. `member-app-parity.md` §6 named it,
 * the settings row pointed at it for three weeks, and a member who bookmarked it should not meet
 * a screen with no password form on it.
 *
 * ## What this page does not do
 *
 * **It does not own the form.** `ChangePasswordForm` is `@h2hspark/auth-ui`'s, unchanged: the
 * package owns what changing a password *is* — `POST /api/auth/password/change`, the policy, the
 * strength meter — and it is the same component the organizer and admin consoles render. A second
 * copy here would be a second place the policy drifts.
 *
 * **It does not restate the consequence in its own words.** `USR-004` AC-3 is that the change ends
 * every other session, and the sentence for it is `MEM_ACCOUNT_COPY.passwordEndsOtherSessions`,
 * which shipped with the inline card and moves here with it. The prototype writes only *"You'll
 * stay signed in on this device"*, which implies the rest without stating it — and the thing being
 * implied is the entire reason somebody changes a password after losing a phone.
 */
export function ChangePasswordPage(): React.ReactElement {
  return (
    <div className="pb-8">
      <PageBackLink to={MEMBER_PROFILE_ROUTES.account}>
        {MEMBER_ACCOUNT_COPY.backToAccount}
      </PageBackLink>

      <h1 className="mb-1.5 text-2xl tracking-tight md:text-4xl">
        {MEMBER_ACCOUNT_COPY.passwordHeading}
      </h1>
      {/* Two sentences: the prototype's, then `USR-004` AC-3's. Capped at the measure the other
          three sub-pages use for their lede, so the four read as one family. */}
      <p className="mb-7 max-w-[700px] text-md leading-relaxed text-muted-foreground md:text-lg">
        {MEMBER_ACCOUNT_COPY.passwordSub} {MEMBER_ACCOUNT_COPY.passwordEndsOtherSessions}
      </p>

      <div className="max-w-[560px] rounded-xl border border-border-subtle bg-card p-[26px] shadow-sm">
        <ChangePasswordForm />
      </div>
    </div>
  );
}
